import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { Plus, Trash2, Save, Edit2, Mail } from "lucide-react";
import "./OrganizationSettings.css";

import { toast } from '../utils/toast.js';
function OrganizationSettings() {
  const { t } = useTranslation();
  const { user, updateUserProfile, inviteTeamMember } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState({});

  // Confirmed/pending team invites — kept separate from stepThreeData.authorizedMembers,
  // which is just the free-text list submitted at registration. This tracks who has
  // actually been emailed and whether they've accepted (see /api/organizations/team/invite).
  const [teamInvites, setTeamInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ fullName: "", role: "", email: "" });
  const [invitingMember, setInvitingMember] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  
  // Step 1: Organization Profile
  const [stepOneData, setStepOneData] = useState({
    nameBlocks: [{ language: "Armenian", name: "" }],
    orgType: [],
    customOrgType: "",
    audiences: [],
    description: "",
    mission: "",
    focusAreas: [],
    customFocusArea: "",
    establishedYear: "",
    legalForm: [],
    customLegalForm: ""
  });

  // Step 2: Contact & Networks
  const [stepTwoData, setStepTwoData] = useState({
    orgContactEmail: "",
    orgContactPhone: "",
    orgSecondaryPhone: "",
    address: "",
    website: "",
    socialLinks: [""],
    networks: [],
    customNetwork: ""
  });

  // Step 3: Team & Security
  const [stepThreeData, setStepThreeData] = useState({
    secureSealText: "",
    secureSealAcknowledged: false,
    authorizedMembers: [{ fullName: "", role: "", email: "" }]
  });

  useEffect(() => {
    if (user?.teamInvites) setTeamInvites(user.teamInvites);
  }, [user]);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteForm.fullName.trim() || !inviteForm.email.trim()) return;
    setInvitingMember(true);
    setInviteMessage("");
    try {
      const result = await inviteTeamMember(inviteForm);
      if (result.success) {
        setInviteMessage(t('orgSettings.inviteSent', 'Invitation sent. They need to confirm it by email before they appear as a team member.'));
        setTeamInvites(result.teamInvites || []);
        setInviteForm({ fullName: "", role: "", email: "" });
      } else {
        setInviteMessage(result.error || t('orgSettings.inviteFailed', 'Failed to send invitation.'));
      }
    } finally {
      setInvitingMember(false);
    }
  };

  useEffect(() => {
    if (user) {
      // The real data this whole 4-tab settings page edits was captured at registration
      // (Reg.jsx -> Regnext.jsx -> Reeg.jsx -> Reeeg.jsx) and stored nested under
      // registrationProfile.organizer.{profile,contact,team,verification} — see
      // buildOrganizerRegistrationProfile in server.js. This previously read from flat
      // top-level fields (user.organizationType, user.vatNumber, etc.) that don't exist on
      // the User schema, so every tab silently showed blank instead of what was submitted.
      const org = user.registrationProfile?.organizer || {};
      const profile = org.profile || {};
      const contact = org.contact || {};
      const team = org.team || {};

      // Load Step 1 data
      setStepOneData({
        nameBlocks: profile.nameBlocks?.length ? profile.nameBlocks : [{ language: "Armenian", name: user.organizationName || "" }],
        orgType: profile.orgType || [],
        customOrgType: "",
        audiences: profile.audiences || [],
        description: profile.description || user.bio || "",
        mission: profile.mission || "",
        focusAreas: profile.focusAreas || [],
        customFocusArea: "",
        establishedYear: profile.establishedYear || "",
        legalForm: profile.legalForm || [],
        customLegalForm: ""
      });

      // Load Step 2 data
      setStepTwoData({
        orgContactEmail: contact.orgContactEmail || contact.email || user.contactEmail || user.email || "",
        orgContactPhone: contact.orgContactPhone || contact.phone || user.phone || "",
        orgSecondaryPhone: "",
        address: contact.address || user.location || "",
        website: contact.website || user.website || "",
        socialLinks: contact.socialLinks?.length ? contact.socialLinks : [""],
        networks: contact.networks || [],
        customNetwork: ""
      });

      // Load Step 3 data
      setStepThreeData({
        secureSealText: team.secureSealText || "",
        secureSealAcknowledged: !!team.secureSealAcknowledged,
        authorizedMembers: team.authorizedMembers?.length ? team.authorizedMembers : [{ fullName: "", role: "", email: "" }]
      });
    }
  }, [user]);

  const toggleEditMode = (section) => {
    setEditMode(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      // Two things happen per save: (1) the full section object is written into
      // registrationProfile.organizer.<section> via the fields the backend's /api/auth/profile
      // handler now understands (registrationProfileSection/registrationProfileData), so
      // nothing is silently dropped like it was when this only sent flat top-level field names
      // the User schema doesn't have; (2) a few fields that the rest of the app already reads
      // from top-level User fields (organizationName, bio, location, phone, website,
      // contactEmail) are mirrored there too, so profile cards/search/etc. stay in sync.
      let updateData = {};
      let registrationProfileSection = null;
      let registrationProfileData = null;

      switch (section) {
        case 'step1':
          updateData = {
            organizationName: stepOneData.nameBlocks[0]?.name || "",
            bio: stepOneData.description,
          };
          registrationProfileSection = 'profile';
          registrationProfileData = stepOneData;
          break;
        case 'step2':
          updateData = {
            contactEmail: stepTwoData.orgContactEmail,
            phone: stepTwoData.orgContactPhone,
            location: stepTwoData.address,
            website: stepTwoData.website,
          };
          registrationProfileSection = 'contact';
          registrationProfileData = { ...stepTwoData, socialLinks: stepTwoData.socialLinks.filter(link => link.trim() !== "") };
          break;
        case 'step3':
          registrationProfileSection = 'team';
          registrationProfileData = {
            secureSealText: stepThreeData.secureSealText,
            secureSealAcknowledged: stepThreeData.secureSealAcknowledged,
            authorizedMembers: stepThreeData.authorizedMembers.filter(m => m.fullName.trim() !== ""),
          };
          break;
      }

      if (registrationProfileSection) {
        updateData.registrationProfileSection = registrationProfileSection;
        updateData.registrationProfileData = JSON.stringify(registrationProfileData);
      }

      const result = await updateUserProfile(updateData);
      if (result.success) {
        setEditMode(prev => ({ ...prev, [section]: false }));
        toast(t('orgSettings.alertSavedSuccess'));
      } else {
        toast(t('orgSettings.alertErrorSaving') + result.error);
      }
    } catch (error) {
      toast(t('orgSettings.alertErrorSavingSettings') + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addNameBlock = () => {
    setStepOneData(prev => ({
      ...prev,
      nameBlocks: [...prev.nameBlocks, { language: "English", name: "" }]
    }));
  };

  const removeNameBlock = (index) => {
    if (stepOneData.nameBlocks.length > 1) {
      setStepOneData(prev => ({
        ...prev,
        nameBlocks: prev.nameBlocks.filter((_, i) => i !== index)
      }));
    }
  };

  const updateNameBlock = (index, field, value) => {
    setStepOneData(prev => {
      const newBlocks = [...prev.nameBlocks];
      newBlocks[index] = { ...newBlocks[index], [field]: value };
      return { ...prev, nameBlocks: newBlocks };
    });
  };

  const addSocialLink = () => {
    setStepTwoData(prev => ({
      ...prev,
      socialLinks: [...prev.socialLinks, ""]
    }));
  };

  const removeSocialLink = (index) => {
    if (stepTwoData.socialLinks.length > 1) {
      setStepTwoData(prev => ({
        ...prev,
        socialLinks: prev.socialLinks.filter((_, i) => i !== index)
      }));
    }
  };

  const addMember = () => {
    setStepThreeData(prev => ({
      ...prev,
      authorizedMembers: [...prev.authorizedMembers, { fullName: "", role: "", email: "" }]
    }));
  };

  const removeMember = (index) => {
    if (stepThreeData.authorizedMembers.length > 1) {
      setStepThreeData(prev => ({
        ...prev,
        authorizedMembers: prev.authorizedMembers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateMember = (index, field, value) => {
    setStepThreeData(prev => {
      const newMembers = [...prev.authorizedMembers];
      newMembers[index] = { ...newMembers[index], [field]: value };
      return { ...prev, authorizedMembers: newMembers };
    });
  };

  const renderStepOne = () => {
    const isEditing = editMode.step1;
    
    return (
      <div className="settings-section-content">
        <div className="section-header-actions">
          <h3>{t('orgSettings.step1Title')}</h3>
          {!isEditing ? (
            <button className="edit-btn" onClick={() => toggleEditMode('step1')}>
              <Edit2 size={16} /> {t('orgSettings.edit')}
            </button>
          ) : (
            <button className="save-btn" onClick={() => handleSave('step1')} disabled={saving}>
              <Save size={16} /> {saving ? t('orgSettings.saving') : t('orgSettings.save')}
            </button>
          )}
        </div>

        <div className="settings-grid">
          <div className="setting-group">
            <label>{t('orgSettings.orgNames')}</label>
            {isEditing ? (
              <>
                {stepOneData.nameBlocks.map((block, index) => (
                  <div key={index} className="name-block-row">
                    <select
                      value={block.language}
                      onChange={(e) => updateNameBlock(index, 'language', e.target.value)}
                      className="input-field"
                    >
                      <option value="Armenian">{t('orgSettings.langArmenian')}</option>
                      <option value="English">{t('orgSettings.langEnglish')}</option>
                      <option value="Russian">{t('orgSettings.langRussian')}</option>
                    </select>
                    <input
                      type="text"
                      value={block.name}
                      onChange={(e) => updateNameBlock(index, 'name', e.target.value)}
                      className="input-field"
                      placeholder={t('orgSettings.orgNamePlaceholder')}
                    />
                    {stepOneData.nameBlocks.length > 1 && (
                      <button type="button" onClick={() => removeNameBlock(index)} className="icon-btn">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addNameBlock} className="ghost-btn">
                  <Plus size={16} /> {t('orgSettings.addName')}
                </button>
              </>
            ) : (
              <div className="display-value">
                {stepOneData.nameBlocks.map((block, i) => (
                  <div key={i}><strong>{block.language}:</strong> {block.name || t('orgSettings.notSet')}</div>
                ))}
              </div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.orgType')}</label>
            {isEditing ? (
              <div className="tags-display">
                {stepOneData.orgType.map((type, i) => (
                  <span key={i} className="tag">{type}</span>
                ))}
                <input
                  type="text"
                  value={stepOneData.customOrgType}
                  onChange={(e) => setStepOneData(prev => ({ ...prev, customOrgType: e.target.value }))}
                  placeholder={t('orgSettings.addCustomTypePlaceholder')}
                  className="input-field"
                />
              </div>
            ) : (
              <div className="display-value">
                {stepOneData.orgType.length > 0 ? stepOneData.orgType.join(', ') : t('orgSettings.notSet')}
              </div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.description')}</label>
            {isEditing ? (
              <textarea
                value={stepOneData.description}
                onChange={(e) => setStepOneData(prev => ({ ...prev, description: e.target.value }))}
                className="textarea-field"
                rows="4"
              />
            ) : (
              <div className="display-value">{stepOneData.description || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.mission')}</label>
            {isEditing ? (
              <textarea
                value={stepOneData.mission}
                onChange={(e) => setStepOneData(prev => ({ ...prev, mission: e.target.value }))}
                className="textarea-field"
                rows="4"
              />
            ) : (
              <div className="display-value">{stepOneData.mission || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.focusAreas')}</label>
            {isEditing ? (
              <div className="tags-display">
                {stepOneData.focusAreas.map((area, i) => (
                  <span key={i} className="tag">{area}</span>
                ))}
              </div>
            ) : (
              <div className="display-value">
                {stepOneData.focusAreas.length > 0 ? stepOneData.focusAreas.join(', ') : t('orgSettings.notSet')}
              </div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.establishedYear')}</label>
            {isEditing ? (
              <input
                type="text"
                value={stepOneData.establishedYear}
                onChange={(e) => setStepOneData(prev => ({ ...prev, establishedYear: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepOneData.establishedYear || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.legalForm')}</label>
            {isEditing ? (
              <div className="tags-display">
                {stepOneData.legalForm.map((form, i) => (
                  <span key={i} className="tag">{form}</span>
                ))}
              </div>
            ) : (
              <div className="display-value">
                {stepOneData.legalForm.length > 0 ? stepOneData.legalForm.join(', ') : t('orgSettings.notSet')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepTwo = () => {
    const isEditing = editMode.step2;
    
    return (
      <div className="settings-section-content">
        <div className="section-header-actions">
          <h3>{t('orgSettings.step2Title')}</h3>
          {!isEditing ? (
            <button className="edit-btn" onClick={() => toggleEditMode('step2')}>
              <Edit2 size={16} /> {t('orgSettings.edit')}
            </button>
          ) : (
            <button className="save-btn" onClick={() => handleSave('step2')} disabled={saving}>
              <Save size={16} /> {saving ? t('orgSettings.saving') : t('orgSettings.save')}
            </button>
          )}
        </div>

        <div className="settings-grid">
          <div className="setting-group">
            <label>{t('orgSettings.contactEmail')}</label>
            {isEditing ? (
              <input
                type="email"
                value={stepTwoData.orgContactEmail}
                onChange={(e) => setStepTwoData(prev => ({ ...prev, orgContactEmail: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepTwoData.orgContactEmail || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.primaryPhone')}</label>
            {isEditing ? (
              <input
                type="tel"
                value={stepTwoData.orgContactPhone}
                onChange={(e) => setStepTwoData(prev => ({ ...prev, orgContactPhone: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepTwoData.orgContactPhone || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.secondaryPhone')}</label>
            {isEditing ? (
              <input
                type="tel"
                value={stepTwoData.orgSecondaryPhone}
                onChange={(e) => setStepTwoData(prev => ({ ...prev, orgSecondaryPhone: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepTwoData.orgSecondaryPhone || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.address')}</label>
            {isEditing ? (
              <input
                type="text"
                value={stepTwoData.address}
                onChange={(e) => setStepTwoData(prev => ({ ...prev, address: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepTwoData.address || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.website')}</label>
            {isEditing ? (
              <input
                type="url"
                value={stepTwoData.website}
                onChange={(e) => setStepTwoData(prev => ({ ...prev, website: e.target.value }))}
                className="input-field"
              />
            ) : (
              <div className="display-value">{stepTwoData.website || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.socialLinks')}</label>
            {isEditing ? (
              <>
                {stepTwoData.socialLinks.map((link, index) => (
                  <div key={index} className="social-link-row">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => {
                        const newLinks = [...stepTwoData.socialLinks];
                        newLinks[index] = e.target.value;
                        setStepTwoData(prev => ({ ...prev, socialLinks: newLinks }));
                      }}
                      className="input-field"
                      placeholder={t('orgSettings.socialLinkPlaceholder')}
                    />
                    {stepTwoData.socialLinks.length > 1 && (
                      <button type="button" onClick={() => removeSocialLink(index)} className="icon-btn">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addSocialLink} className="ghost-btn">
                  <Plus size={16} /> {t('orgSettings.addLink')}
                </button>
              </>
            ) : (
              <div className="display-value">
                {stepTwoData.socialLinks.filter(l => l.trim() !== "").length > 0
                  ? stepTwoData.socialLinks.filter(l => l.trim() !== "").map((link, i) => (
                      <div key={i}><a href={link} target="_blank" rel="noopener noreferrer">{link}</a></div>
                    ))
                  : t('orgSettings.noSocialLinks')}
              </div>
            )}
          </div>

          <div className="setting-group">
            <label>{t('orgSettings.networks')}</label>
            {isEditing ? (
              <div className="tags-display">
                {stepTwoData.networks.map((network, i) => (
                  <span key={i} className="tag">{network}</span>
                ))}
              </div>
            ) : (
              <div className="display-value">
                {stepTwoData.networks.length > 0 ? stepTwoData.networks.join(', ') : t('orgSettings.notSet')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepThree = () => {
    const isEditing = editMode.step3;
    
    return (
      <div className="settings-section-content">
        <div className="section-header-actions">
          <h3>{t('orgSettings.step3Title')}</h3>
          {!isEditing ? (
            <button className="edit-btn" onClick={() => toggleEditMode('step3')}>
              <Edit2 size={16} /> {t('orgSettings.edit')}
            </button>
          ) : (
            <button className="save-btn" onClick={() => handleSave('step3')} disabled={saving}>
              <Save size={16} /> {saving ? t('orgSettings.saving') : t('orgSettings.save')}
            </button>
          )}
        </div>

        <div className="setting-group full-width" style={{ marginBottom: 20 }}>
          <label>{t('orgSettings.inviteMemberTitle', 'Invite a team member')}</label>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "#888" }}>
            {t('orgSettings.inviteMemberSubtitle', "They'll get an email and only appear in your team once they accept it.")}
          </p>
          <form onSubmit={handleInviteMember} className="member-card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input type="text" placeholder={t('orgSettings.fullNamePlaceholder')} value={inviteForm.fullName}
              onChange={(e) => setInviteForm(prev => ({ ...prev, fullName: e.target.value }))} className="input-field" required />
            <input type="text" placeholder={t('orgSettings.rolePlaceholder')} value={inviteForm.role}
              onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))} className="input-field" />
            <input type="email" placeholder={t('orgSettings.emailPlaceholder')} value={inviteForm.email}
              onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))} className="input-field" required />
            <button type="submit" className="save-btn" disabled={invitingMember} style={{ alignSelf: "flex-start" }}>
              <Mail size={16} /> {invitingMember ? t('orgSettings.saving') : t('orgSettings.sendInvite', 'Send invite')}
            </button>
            {inviteMessage && <div className="display-value">{inviteMessage}</div>}
          </form>

          {teamInvites.length > 0 && (
            <div className="members-display" style={{ marginTop: 14 }}>
              {teamInvites.map((invite, i) => (
                <div key={i} className="member-display-card">
                  <strong>{invite.fullName}</strong>
                  <div>{invite.role}</div>
                  <div>{invite.email}</div>
                  <div>
                    {invite.status === 'accepted' && `✅ ${t('orgSettings.inviteAccepted', 'Confirmed')}`}
                    {invite.status === 'pending' && `⏳ ${t('orgSettings.invitePending', 'Awaiting confirmation')}`}
                    {invite.status === 'declined' && `❌ ${t('orgSettings.inviteDeclined', 'Declined')}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="settings-grid">
          <div className="setting-group">
            <label>{t('orgSettings.secureSealText')}</label>
            {isEditing ? (
              <textarea
                value={stepThreeData.secureSealText}
                onChange={(e) => setStepThreeData(prev => ({ ...prev, secureSealText: e.target.value }))}
                className="textarea-field"
                rows="3"
              />
            ) : (
              <div className="display-value">{stepThreeData.secureSealText || t('orgSettings.notSet')}</div>
            )}
          </div>

          <div className="setting-group full-width">
            <label>{t('orgSettings.authorizedMembers')}</label>
            {isEditing ? (
              <>
                {stepThreeData.authorizedMembers.map((member, index) => (
                  <div key={index} className="member-card">
                    <div className="member-header">
                      <h4>{t('orgSettings.memberNum', { n: index + 1 })}</h4>
                      {stepThreeData.authorizedMembers.length > 1 && (
                        <button type="button" onClick={() => removeMember(index)} className="icon-btn">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={member.fullName}
                      onChange={(e) => updateMember(index, 'fullName', e.target.value)}
                      placeholder={t('orgSettings.fullNamePlaceholder')}
                      className="input-field"
                    />
                    <input
                      type="text"
                      value={member.role}
                      onChange={(e) => updateMember(index, 'role', e.target.value)}
                      placeholder={t('orgSettings.rolePlaceholder')}
                      className="input-field"
                    />
                    <input
                      type="email"
                      value={member.email}
                      onChange={(e) => updateMember(index, 'email', e.target.value)}
                      placeholder={t('orgSettings.emailPlaceholder')}
                      className="input-field"
                    />
                  </div>
                ))}
                <button type="button" onClick={addMember} className="ghost-btn">
                  <Plus size={16} /> {t('orgSettings.addMember')}
                </button>
              </>
            ) : (
              <div className="members-display">
                {stepThreeData.authorizedMembers.filter(m => m.fullName.trim() !== "").length > 0 ? (
                  stepThreeData.authorizedMembers.filter(m => m.fullName.trim() !== "").map((member, i) => (
                    <div key={i} className="member-display-card">
                      <strong>{member.fullName}</strong>
                      <div>{member.role}</div>
                      <div>{member.email}</div>
                    </div>
                  ))
                ) : (
                  <div className="display-value">{t('orgSettings.noMembers')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>{t('orgSettings.pageTitle')}</h1>
        <p>{t('orgSettings.pageSubtitle')}</p>
      </div>

      <div className="settings-layout">
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          <button
            className={`sidebar-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <span className="tab-icon">📋</span>
            {t('orgSettings.tabProfile')}
          </button>
          <button
            className={`sidebar-tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <span className="tab-icon">📞</span>
            {t('orgSettings.tabContact')}
          </button>
          <button
            className={`sidebar-tab ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            <span className="tab-icon">👥</span>
            {t('orgSettings.tabTeam')}
          </button>
        </div>

        {/* Main Content */}
        <div className="settings-content">
          {activeTab === 'profile' && renderStepOne()}
          {activeTab === 'contact' && renderStepTwo()}
          {activeTab === 'team' && renderStepThree()}
        </div>
      </div>
    </div>
  );
}

export default OrganizationSettings;

